import type {
    DropdownData,
    ReactiveFormSupportDataDiagnosticsThreshold,
    ReactiveFormSupportDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormSupportDataSourceFactory } from '@fitogest/reactive-form-builder/utils';
import type { ClienteModel } from 'src/app/models/cliente.model';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { EntidadModel } from 'src/app/models/reto/entidad.model';
import type { TransaccionModel } from 'src/app/models/reto/transaccion.model';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import {
    TRANSACCIONES_FORM_SUPPORT_DATA_KEYS,
    type TransaccionesFormSupportDataKey,
} from './form-control/transacciones-form-load-plan';
import type { TransaccionesFormDefinition } from './form-control/form-definition';

interface TransaccionesSupportDataEndpoints {
    readonly productosGET: RequestHandler<ProductosModel[]>;
    readonly entidadesGET: RequestHandler<EntidadModel[]>;
    readonly clientesGET: RequestHandler<ClienteModel[]>;
}

export interface TransaccionesSupportDataSourcesFactoryContext {
    readonly form: TransaccionesFormDefinition;
    readonly hasClients: boolean;
    readonly endpoints: TransaccionesSupportDataEndpoints;
    readonly setProducts: (products: ProductosModel[]) => void;
    readonly setEntities: (entities: EntidadModel[]) => void;
}

const TRANSACCIONES_SUPPORT_DATA_TTL_MS = 600000;
const TRANSACCIONES_SUPPORT_DATA_STALE_TTL_MS = 86400000;

const DEFAULT_TRANSACCIONES_DIAGNOSTICS_THRESHOLD: ReactiveFormSupportDataDiagnosticsThreshold = {
    maxAverageMs: 500,
    maxMs: 1500,
    maxAverageNetworkMs: 1200,
    maxCacheMs: 250,
};

const TRANSACCIONES_SOURCE_THRESHOLDS: Partial<Record<TransaccionesFormSupportDataKey, ReactiveFormSupportDataDiagnosticsThreshold>> = {
    [TRANSACCIONES_FORM_SUPPORT_DATA_KEYS.productos]: {
        maxAverageMs: 600,
        maxMs: 1800,
        maxAverageNetworkMs: 1400,
        maxCacheMs: 250,
    },
    [TRANSACCIONES_FORM_SUPPORT_DATA_KEYS.entidades]: {
        maxAverageMs: 700,
        maxMs: 2000,
        maxAverageNetworkMs: 1600,
        maxCacheMs: 250,
    },
};

/** Creates declarative support-data sources for the Transacciones form. */
export class TransaccionesSupportDataSourcesFactory {
    public static create(
        context: TransaccionesSupportDataSourcesFactoryContext,
    ): readonly ReactiveFormSupportDataSource<TransaccionModel>[] {
        const factory = new ReactiveFormSupportDataSourceFactory<TransaccionModel, TransaccionesFormSupportDataKey>({
            ttlMs: TRANSACCIONES_SUPPORT_DATA_TTL_MS,
            staleTtlMs: TRANSACCIONES_SUPPORT_DATA_STALE_TTL_MS,
            cacheStorage: 'persistent',
            staleWhileRevalidate: true,
            diagnosticsThreshold: (key) => TransaccionesSupportDataSourcesFactory.resolveDiagnosticsThreshold(key),
        });
        const sources: ReactiveFormSupportDataSource<TransaccionModel>[] = [
            factory.source({
                key: TRANSACCIONES_FORM_SUPPORT_DATA_KEYS.productos,
                phase: 'critical',
                endpoint: context.endpoints.productosGET,
                cacheKey: 'transacciones.productos',
                onSuccess: (result) => {
                    context.setProducts(TransaccionesSupportDataSourcesFactory.toProducts(result));
                },
            }),
            factory.source({
                key: TRANSACCIONES_FORM_SUPPORT_DATA_KEYS.entidades,
                phase: 'critical',
                endpoint: context.endpoints.entidadesGET,
                cacheKey: 'transacciones.entidades',
                onSuccess: (result) => {
                    context.setEntities(TransaccionesSupportDataSourcesFactory.toEntities(result));
                },
            }),
        ];

        if (context.hasClients) {
            sources.push(factory.dropdownSource({
                key: TRANSACCIONES_FORM_SUPPORT_DATA_KEYS.clientes,
                phase: 'critical',
                endpoint: context.endpoints.clientesGET,
                target: context.form.lists.clientes as DropdownData<unknown>,
                label: { or: ['razon_social', 'nombre'] },
                cacheKey: 'transacciones.clientes',
            }));
        }

        return sources;
    }

    private static resolveDiagnosticsThreshold(
        key: TransaccionesFormSupportDataKey,
    ): ReactiveFormSupportDataDiagnosticsThreshold {
        return TRANSACCIONES_SOURCE_THRESHOLDS[key] ?? DEFAULT_TRANSACCIONES_DIAGNOSTICS_THRESHOLD;
    }

    private static toProducts(value: unknown): ProductosModel[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value.filter((item): item is ProductosModel => item !== null && typeof item === 'object');
    }

    private static toEntities(value: unknown): EntidadModel[] {
        if (!Array.isArray(value)) {
            return [];
        }

        return value.filter((item): item is EntidadModel => item !== null && typeof item === 'object');
    }
}
